import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import BackgroundEffects from "./components/layout/BackgroundEffects";
import DragScrollController from "./components/layout/DragScrollController";
import MessageDock from "./components/messages/MessageDock";
import SiteFooter from "./components/layout/SiteFooter";
import SiteHeader from "./components/layout/SiteHeader";
import { useAuth } from "./context/AuthContext";
import { appendReturnPath, buildLoginRedirect, getReturnPathFromSearch, isAliasProfilePath, PUBLIC_PATHS } from "./routing/accessControl";

const UserProfileViewPage = lazy(() => import("./pages/UserProfileViewPage"));

const pageComponents = {
  "/": lazy(() => import("./pages/HomePage")),
  "/kesfet": lazy(() => import("./pages/ExplorePage")),
  "/arama": lazy(() => import("./pages/SearchPage")),
  "/etkinlik-olustur": lazy(() => import("./pages/CreateEventPage")),
  "/etkinlik-detay": lazy(() => import("./pages/EventDetailPage")),
  "/harita": lazy(() => import("./pages/MapPage")),
  "/squadlar": lazy(() => import("./pages/SquadsPage")),
  "/bildirimler": lazy(() => import("./pages/NotificationsPage")),
  "/profilim": lazy(() => import("./pages/ProfilePage")),
  "/kullanici-profili": UserProfileViewPage,
  "/profili-duzenle": lazy(() => import("./pages/EditProfilePage")),
  "/skilltree": lazy(() => import("./pages/SkillTreePage")),
  "/skilltree2": lazy(() => import("./pages/SkillTree2Page")),
  "/skilltree3": lazy(() => import("./pages/SkillTree3Page")),
  "/skilltree4": lazy(() => import("./pages/SkillTree4Page")),
  "/ayarlar": lazy(() => import("./pages/SettingsPage")),
  "/ayar-detay": lazy(() => import("./pages/SettingsDetailPage")),
  "/telefon-dogrulama": lazy(() => import("./pages/PhoneVerificationPage")),
  "/email-dogrulama": lazy(() => import("./pages/EmailVerificationPage")),
  "/eposta-dogrulama": lazy(() => import("./pages/EmailVerificationPage")),
  "/host": lazy(() => import("./pages/HostPage")),
  "/admin-ajanda": lazy(() => import("./pages/AdminPlannerPage")),
  "/hakkimizda": lazy(() => import("./pages/AboutPage")),
  "/topluluk-ilkeleri": lazy(() => import("./pages/CommunityGuidelinesPage")),
  "/moderatorluk": lazy(() => import("./pages/ModerationPage")),
  "/partnerlik": lazy(() => import("./pages/PartnershipPage")),
  "/sosyal-medya": lazy(() => import("./pages/SocialMediaPage")),
  "/yasal": lazy(() => import("./pages/LegalPage")),
  "/privacy": lazy(() => import("./pages/PrivacyPage")),
  "/policy": lazy(() => import("./pages/PrivacyPage")),
  "/gizlilik-politikasi": lazy(() => import("./pages/PrivacyPage")),
  "/terms": lazy(() => import("./pages/TermsPage")),
  "/kullanim-kosullari": lazy(() => import("./pages/TermsPage")),
  "/giris": lazy(() => import("./pages/AuthPage")),
  "/kayit": lazy(() => import("./pages/SignupPage")),
  "/kayit-ol": lazy(() => import("./pages/SignupPage")),
  "/sifremi-unuttum": lazy(() => import("./pages/ForgotPasswordPage")),
};

const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const emailVerificationPaths = new Set(["/email-dogrulama", "/eposta-dogrulama"]);
const authEntryPaths = new Set(["/giris", "/kayit", "/kayit-ol"]);
const authScreenPaths = new Set(["/giris", "/kayit", "/kayit-ol", "/sifremi-unuttum", ...emailVerificationPaths]);

function RouteLoading() {
  return (
    <main className="page route-loading-page" aria-busy="true">
      <div className="flow-web-loading-card">
        <span className="flow-web-spinner" />
        <div>
          <strong>Sayfa Yükleniyor</strong>
          <p>İlgili ekran paketi hazırlanıyor.</p>
        </div>
      </div>
    </main>
  );
}

function getCurrentPath() {
  if (typeof window === "undefined") return { pathname: "/", search: "", hash: "" };
  return {
    pathname: window.location.pathname || "/",
    search: window.location.search || "",
    hash: window.location.hash || "",
  };
}

export default function App() {
  const { user, isEmailVerified, loading: authLoading } = useAuth();
  const [currentPath, setCurrentPath] = useState(getCurrentPath);
  const Page = useMemo(() => pageComponents[currentPath.pathname] || (isAliasProfilePath(currentPath.pathname) ? UserProfileViewPage : NotFoundPage), [currentPath.pathname]);
  const isSignedIn = Boolean(user?.uid);
  const isPublicPath = PUBLIC_PATHS.has(currentPath.pathname);
  const shouldRedirectToLogin = !authLoading && !isSignedIn && !isPublicPath;
  const shouldRenderPage = !authLoading && !shouldRedirectToLogin && (isSignedIn || currentPath.pathname !== "/");
  const shouldShowAuthenticatedShell = !authLoading && isSignedIn;
  const shouldShowMessageDock = shouldShowAuthenticatedShell && !authScreenPaths.has(currentPath.pathname);
  const requiresEmailVerification = Boolean(user?.uid && !isEmailVerified && !user.emailVerified);
  const authEntryReturnPath = authEntryPaths.has(currentPath.pathname)
    ? getReturnPathFromSearch(currentPath.search)
    : "/";

  const navigate = (pathWithSearch, options = {}) => {
    const nextPath = pathWithSearch || "/";
    if (typeof window !== "undefined") {
      const parsed = new URL(nextPath, window.location.origin);
      const guardedTarget = !authLoading && !isSignedIn && !PUBLIC_PATHS.has(parsed.pathname)
        ? buildLoginRedirect({ pathname: parsed.pathname, search: parsed.search, hash: parsed.hash })
        : `${parsed.pathname}${parsed.search}${parsed.hash}`;
      const guardedUrl = new URL(guardedTarget, window.location.origin);
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const target = `${guardedUrl.pathname}${guardedUrl.search}${guardedUrl.hash}`;

      if (current !== target) {
        const method = options.replace ? "replaceState" : "pushState";
        window.history[method]({ path: target }, "", target);
      }

      if (guardedUrl.hash) {
        window.setTimeout(() => {
          const sectionId = guardedUrl.hash.replace("#", "");
          const section = document.getElementById(sectionId);
          if (section) {
            section.scrollIntoView({ behavior: "smooth", block: "start" });
            return;
          }
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 40);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
    setCurrentPath(getCurrentPath());
  };

  useEffect(() => {
    if (!shouldRedirectToLogin) return;
    navigate(buildLoginRedirect(currentPath), { replace: true });
  }, [currentPath.hash, currentPath.pathname, currentPath.search, shouldRedirectToLogin]);

  useEffect(() => {
    if (authLoading || !isSignedIn || !authEntryPaths.has(currentPath.pathname)) return;
    const targetPath = requiresEmailVerification
      ? appendReturnPath("/email-dogrulama", authEntryReturnPath)
      : authEntryReturnPath;
    navigate(targetPath, { replace: true });
  }, [authEntryReturnPath, authLoading, currentPath.pathname, isSignedIn, requiresEmailVerification]);

  useEffect(() => {
    if (authLoading || !requiresEmailVerification || emailVerificationPaths.has(currentPath.pathname)) return;
    if (authEntryPaths.has(currentPath.pathname)) return;
    if (PUBLIC_PATHS.has(currentPath.pathname)) return;
    const currentTarget = `${currentPath.pathname}${currentPath.search}${currentPath.hash}`;
    const returnParam = currentTarget && currentTarget !== "/" ? `?return=${encodeURIComponent(currentTarget)}` : "";
    navigate(`/email-dogrulama${returnParam}`, { replace: true });
  }, [authLoading, currentPath.hash, currentPath.pathname, currentPath.search, requiresEmailVerification]);

  useEffect(() => {
    const handlePopState = () => setCurrentPath(getCurrentPath());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return (
    <div className="site-shell">
      <DragScrollController />
      <BackgroundEffects />
      {!authLoading ? <SiteHeader currentPath={currentPath.pathname} currentSearch={currentPath.search} onNavigate={navigate} /> : null}
      {shouldRenderPage ? (
        <div className="page-transition" key={`${currentPath.pathname}${currentPath.search}${currentPath.hash}`}>
          <Suspense fallback={<RouteLoading />}>
            <Page onNavigate={navigate} currentPathname={currentPath.pathname} currentSearch={currentPath.search} currentHash={currentPath.hash} />
          </Suspense>
        </div>
      ) : null}
      {shouldShowMessageDock ? <MessageDock onNavigate={navigate} /> : null}
      {shouldShowAuthenticatedShell ? <SiteFooter onNavigate={navigate} /> : null}
    </div>
  );
}
